import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useAdminInviteCodes, useGenerateInviteCode, useDeleteInviteCode } from '@/hooks/useAdmin';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';
import { Plus, Loader2, Copy, Trash2, Check, Ticket, ChevronDown } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';

export default function AdminInvites() {
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [showUsedCodes, setShowUsedCodes] = useState(false);
  const { user } = useAuth();
  const { data: inviteCodes, isLoading } = useAdminInviteCodes();
  const generateCode = useGenerateInviteCode();
  const deleteCode = useDeleteInviteCode();
  const { toast } = useToast();

  const handleGenerateCode = async () => {
    if (!user?.id) return;

    try {
      const code = await generateCode.mutateAsync(user.id);
      toast({
        title: 'Invite code generated',
        description: `Code: ${code.code}`,
      });
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      });
    }
  };

  const handleCopyCode = async (code: string, id: string) => {
    await navigator.clipboard.writeText(code);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
    toast({
      title: 'Copied!',
      description: 'Invite code copied to clipboard.',
    });
  };

  const handleDeleteCode = async (id: string) => {
    if (!confirm('Are you sure you want to delete this invite code?')) return;

    try {
      await deleteCode.mutateAsync(id);
      toast({
        title: 'Code deleted',
        description: 'The invite code has been removed.',
      });
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      });
    }
  };

  const unusedCodes = inviteCodes?.filter((c) => !c.is_used) || [];
  const usedCodes = inviteCodes?.filter((c) => c.is_used) || [];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-display font-bold text-foreground">
            Invite Codes
          </h1>
          <p className="text-muted-foreground text-sm">
            Generate and manage invitation codes
          </p>
        </div>
        <Button onClick={handleGenerateCode} disabled={generateCode.isPending}>
          {generateCode.isPending ? (
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
          ) : (
            <Plus className="h-4 w-4 mr-2" />
          )}
          Generate Code
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Total Codes</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{inviteCodes?.length || 0}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-green-600">
              Available
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">
              {unusedCodes.length}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Used
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-muted-foreground">
              {usedCodes.length}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Available Codes */}
      <Card>
        <CardHeader>
          <CardTitle>Available Codes</CardTitle>
          <CardDescription>
            Unused invite codes ready to share
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          ) : unusedCodes.length === 0 ? (
            <div className="text-center py-12">
              <Ticket className="h-12 w-12 text-muted-foreground/50 mx-auto mb-4" />
              <p className="text-muted-foreground">
                No available codes. Generate one above.
              </p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Code</TableHead>
                  <TableHead>Created By</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {unusedCodes.map((code) => (
                  <TableRow key={code.id}>
                    <TableCell>
                      <code className="px-2 py-1 bg-muted rounded text-sm font-mono">
                        {code.code}
                      </code>
                    </TableCell>
                    <TableCell className="text-sm">
                      {code.referrer_name || <span className="text-muted-foreground">Admin</span>}
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {formatDistanceToNow(new Date(code.created_at), {
                        addSuffix: true,
                      })}
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleCopyCode(code.code, code.id)}
                        >
                          {copiedId === code.id ? (
                            <Check className="h-4 w-4 text-green-600" />
                          ) : (
                            <Copy className="h-4 w-4" />
                          )}
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteCode(code.id)}
                        >
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Used Codes - Collapsible */}
      {usedCodes.length > 0 && (
        <Collapsible open={showUsedCodes} onOpenChange={setShowUsedCodes}>
          <Card>
            <CollapsibleTrigger asChild>
              <CardHeader className="cursor-pointer hover:bg-muted/50 transition-colors">
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-base">Used Codes ({usedCodes.length})</CardTitle>
                    <CardDescription>
                      Previously used invite codes
                    </CardDescription>
                  </div>
                  <ChevronDown
                    className={`h-5 w-5 text-muted-foreground transition-transform ${
                      showUsedCodes ? 'rotate-180' : ''
                    }`}
                  />
                </div>
              </CardHeader>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <CardContent className="pt-0">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Code</TableHead>
                      <TableHead>Used By</TableHead>
                      <TableHead>Used</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {usedCodes.map((code) => (
                      <TableRow key={code.id}>
                        <TableCell>
                          <code className="px-2 py-1 bg-muted rounded text-sm font-mono text-muted-foreground">
                            {code.code}
                          </code>
                        </TableCell>
                        <TableCell className="text-sm">
                          {code.invitee_name || <span className="text-muted-foreground">-</span>}
                        </TableCell>
                        <TableCell className="text-muted-foreground text-sm">
                          {code.used_at
                            ? formatDistanceToNow(new Date(code.used_at), {
                                addSuffix: true,
                              })
                            : '-'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </CollapsibleContent>
          </Card>
        </Collapsible>
      )}
    </div>
  );
}
